import { useState, useCallback } from 'react';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadState {
  isUploading: boolean;
  progress: UploadProgress | null;
  error: string | null;
  retryCount: number;
  maxRetries: number;
}

export interface UploadOptions {
  maxRetries?: number;
  retryDelay?: number;
  onProgress?: (progress: UploadProgress) => void;
  onSuccess?: (response: any) => void;
  onError?: (error: string) => void;
}

const DEFAULT_OPTIONS: Required<UploadOptions> = {
  maxRetries: 3,
  retryDelay: 1000,
  onProgress: () => {},
  onSuccess: () => {},
  onError: () => {},
};

/**
 * Hook for handling photo uploads with progress tracking and retry logic
 */
export function usePhotoUpload(options: UploadOptions = {}) {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: null,
    error: null,
    retryCount: 0,
    maxRetries: opts.maxRetries,
  });

  // Calculate exponential backoff delay
  const calculateRetryDelay = (retryCount: number): number => {
    return opts.retryDelay * Math.pow(2, retryCount);
  };

  // Sleep utility for retry delays
  const sleep = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  // Create XMLHttpRequest with progress tracking and authentication
  const createUploadRequest = async (
    url: string,
    formData: FormData,
    onProgress: (progress: UploadProgress) => void
  ): Promise<any> => {
    // Import supabase dynamically to avoid circular dependencies
    const { supabase } = await import('@/lib/supabase');
    
    return new Promise(async (resolve, reject) => {
      // Get current session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        reject(new Error('Authentication required. Please sign in to upload photos.'));
        return;
      }

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress: UploadProgress = {
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100),
          };
          onProgress(progress);
        }
      });

      // Handle successful response
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            resolve(xhr.responseText);
          }
        } else if (xhr.status === 401) {
          reject(new Error('Authentication failed. Please sign in again.'));
        } else if (xhr.status === 403) {
          reject(new Error('Access denied. You can only upload your own photos.'));
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });

      // Handle network errors
      xhr.addEventListener('error', () => {
        reject(new Error('Network error occurred during upload'));
      });

      // Handle timeout
      xhr.addEventListener('timeout', () => {
        reject(new Error('Upload request timed out'));
      });

      // Configure and send request with authentication
      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.timeout = 60000; // 60 second timeout
      xhr.send(formData);
    });
  };

  // Upload with retry logic
  const uploadWithRetry = useCallback(async (
    url: string,
    formData: FormData,
    retryCount: number = 0
  ): Promise<any> => {
    try {
      const response = await createUploadRequest(url, formData, (progress) => {
        setUploadState(prev => ({ ...prev, progress }));
        opts.onProgress(progress);
      });

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      // Check if we should retry
      if (retryCount < opts.maxRetries) {
        const delay = calculateRetryDelay(retryCount);
        
        setUploadState(prev => ({
          ...prev,
          error: `${errorMessage}. Retrying in ${Math.ceil(delay / 1000)}s... (${retryCount + 1}/${opts.maxRetries})`,
          retryCount: retryCount + 1,
        }));

        await sleep(delay);
        return uploadWithRetry(url, formData, retryCount + 1);
      } else {
        throw new Error(`${errorMessage} (Failed after ${opts.maxRetries} retries)`);
      }
    }
  }, [opts]);

  // Main upload function
  const uploadPhoto = useCallback(async (file: File, additionalData?: Record<string, any>) => {
    // Reset state
    setUploadState({
      isUploading: true,
      progress: null,
      error: null,
      retryCount: 0,
      maxRetries: opts.maxRetries,
    });

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('file', file); // Backend expects 'file' field name
      
      // Add additional data if provided
      if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
          formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
        });
      }

      // Get API base URL
      const API_BASE = import.meta.env.PROD ? 'https://altitude.a-3b3ziz.workers.dev/api' : 'http://localhost:8787/api';
      
      // Upload with retry logic
      const response = await uploadWithRetry(`${API_BASE}/photos`, formData);

      // Success
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        progress: { loaded: file.size, total: file.size, percentage: 100 },
      }));

      opts.onSuccess(response);
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        error: errorMessage,
      }));

      opts.onError(errorMessage);
      throw error;
    }
  }, [uploadWithRetry, opts]);

  // Reset upload state
  const resetUpload = useCallback(() => {
    setUploadState({
      isUploading: false,
      progress: null,
      error: null,
      retryCount: 0,
      maxRetries: opts.maxRetries,
    });
  }, [opts.maxRetries]);

  // Retry current upload (if there's an error)
  const retryUpload = useCallback(() => {
    if (uploadState.error && !uploadState.isUploading) {
      // This would need to store the last upload parameters to retry
      // For now, just reset the error state
      setUploadState(prev => ({
        ...prev,
        error: null,
        retryCount: 0,
      }));
    }
  }, [uploadState.error, uploadState.isUploading]);

  return {
    uploadState,
    uploadPhoto,
    resetUpload,
    retryUpload,
  };
}